import { zodResolver } from '@hookform/resolvers/zod';
import { CrmTaskStatus } from '@plato/data/prisma';
import { ArrowUpRight, CalendarIcon, CircleUserRoundIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ModalForm } from '../../../components/form-components/modal-form.tsx';
import { Label } from '../../../components/ui/label.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { crmTaskInputDtoSchema } from '../../../server/dto/crm-task-input-dto.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { DueDatePicker, TaskLinksInput } from './task-form-fields.tsx';

type TaskFormDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: CrmTaskInputDTO) => void | Promise<void>;
    title: string;
    initialValues?: Partial<CrmTaskInputDTO>;
    hideRelatedDetailsLink?: boolean;
};

export const TaskFormDialog = ({
    open,
    onClose,
    onSubmit,
    title,
    initialValues,
    hideRelatedDetailsLink,
}: TaskFormDialogProps): ReactNode => {
    const { t } = useLocale('tasks');
    const appState = useAppState();
    const { enableFileUpload } = useAppConfig();
    const { currentSessionId } = useVoiceRecording();

    const defaultValues = useMemo(() => {
        return {
            dueDate: new Date(),
            links: [],
            assignedToId: appState.platoUser.id,
            assignedById: appState.platoUser.id || '',
            createdById: appState.platoUser.id || '',
            status: CrmTaskStatus.OPEN,
            description: undefined,
            attachments: [],
            ...initialValues,
        };
    }, [appState.platoUser.id, initialValues]);

    const form = useForm<CrmTaskInputDTO>({
        defaultValues,
        resolver: zodResolver(crmTaskInputDtoSchema),
    });

    const {
        control,
        handleSubmit,
        formState: { isValid: isFormValid, isDirty, isSubmitting, errors: formErrors },
    } = form;

    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(() => {
            onClose();
        });
    };

    const submit = handleSubmit(async (values) => {
        try {
            await onSubmit(values);
            onClose();
        } catch {
            // caller surfaces the error (e.g. toast); keep the dialog open
        }
    });

    return (
        <Dialog showClose open={open} onClose={handleClose}>
            <DialogForm>
                <DialogForm.Header title={title} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <DialogForm.Item>
                            <Label htmlFor="description" className="sr-only">
                                {t('description')}
                            </Label>
                            <DialogForm.Body>
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field }) => (
                                        <RichTextInput
                                            trackingContext={{ source: 'task' }}
                                            placeholder={t('tasks__description_placeholder')}
                                            {...field}
                                        />
                                    )}
                                />
                            </DialogForm.Body>
                            <DialogForm.Error error={formErrors.description} />
                        </DialogForm.Item>
                    </DialogForm.ItemList>
                    <DialogForm.Group>
                        <DialogForm.ItemList subgrid>
                            <DialogForm.Item>
                                <DialogForm.Label icon={ArrowUpRight}>
                                    {t('related_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex-col">
                                    <Controller
                                        control={control}
                                        name="links"
                                        render={({ field }) => (
                                            <TaskLinksInput
                                                {...field}
                                                hideRelatedDetailsLink={hideRelatedDetailsLink}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                                <ModalForm.Error errorMessage={formErrors.links} />
                            </DialogForm.Item>

                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                                    {t('due_date', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({ field }) => <DueDatePicker {...field} />}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.dueDate} />
                            </DialogForm.Item>
                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="assignedToId" icon={CircleUserRoundIcon}>
                                    {t('assigned_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="assignedToId"
                                        render={({ field }) => (
                                            <UsersSelect {...field} className="w-full justify-start" />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.assignedToId} />
                            </DialogForm.Item>
                        </DialogForm.ItemList>
                        {enableFileUpload && (
                            <DialogForm.ItemList subgrid>
                                <Controller
                                    control={control}
                                    name="attachments"
                                    render={({ field }) => (
                                        <FileUploader
                                            {...field}
                                            value={field.value ?? []}
                                            analyticsContext="task"
                                        />
                                    )}
                                />
                            </DialogForm.ItemList>
                        )}
                    </DialogForm.Group>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    isLoading={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={submit}
                    disabled={!isFormValid || !!currentSessionId}
                />
            </DialogForm>
        </Dialog>
    );
};
